import { useMemo, useState } from "react";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import styled from "@emotion/styled";

import Button from "components/ui/button/Button";
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "components/ui/combobox";
import type { Option, OptionValue } from "components/ui/combobox/types";
import { useDebouncedValue } from "../../../lib/debounce";
import { NoticesSearchRequest } from "../../../lib/generated/app-service-gql/graphql";
import { useNoticesSimple } from "../../../lib/hooks/api/notices/useNoticesSimple";
import { NoticesSort, NoticesSortOrder } from "../../../lib/types/graphQLEnums";

export type TenderComboBoxProps<TFieldValues extends FieldValues = FieldValues> = {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  placeholder?: string;
  disabled?: boolean;
  defaultNoticeRequest?: Partial<NoticesSearchRequest>;
};

export function TenderCombobox<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  placeholder = "Search tenders...",
  disabled = false,
  defaultNoticeRequest,
}: TenderComboBoxProps<TFieldValues>) {
  const { field } = useController({ control, name });
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchText, 300);

  const { data, isLoading } = useNoticesSimple({
    ...defaultNoticeRequest,
    searchText: debouncedSearch || undefined,
    sort: NoticesSort.Score,
    sortOrder: NoticesSortOrder.Desc,
    page: 1,
    limit: 100,
  });

  const items = useMemo<Option[]>(
    () =>
      (data?.results ?? []).map((notice) => ({
        label: notice.name ?? "",
        value: notice.procurementStage.id,
      })),
    [data],
  );

  // Bridge: form field is a single UUID string | null, Combobox expects OptionValue[]
  const comboboxValue: OptionValue[] = field.value ? [field.value] : [];
  const handleChange = (newValues: OptionValue[]) => {
    field.onChange(newValues[0] ?? null);
  };

  return (
    <Combobox items={items} value={comboboxValue} onChange={handleChange}>
      {(combobox) => (
        <ComboboxContent
          trigger={
            <Button buttonType="secondary" icon="plus" fullWidth disabled={disabled}>
              Add a tender
            </Button>
          }
          open={combobox.open}
          setOpen={combobox.setOpen}
        >
          <ComboboxInput
            placeholder={placeholder}
            filterValue={combobox.filterValue}
            setFilterValue={(value) => {
              combobox.setFilterValue(value);
              setSearchText(value);
            }}
            open={combobox.open}
            setOpen={combobox.setOpen}
            value={combobox.value}
            items={combobox.items}
            multiple={combobox.multiple}
            inputRef={combobox.inputRef}
            inputElementRef={combobox.inputElementRef}
            disabled={disabled}
          />
          <ComboboxEmpty
            filteredItems={combobox.filteredItems}
            filterValue={combobox.filterValue}
            disableAddingOptions
          >
            {isLoading ? "Loading..." : "No tenders found."}
          </ComboboxEmpty>
          <ListScrollArea>
            <ComboboxList items={combobox.items} filteredItems={combobox.filteredItems}>
              <ComboboxCollection options={combobox.filteredItems as Option[]}>
                {(option: Option) => (
                  <ComboboxItem
                    key={String(option.value)}
                    option={option}
                    value={combobox.value}
                    selectItem={combobox.selectItem}
                    multiple={combobox.multiple}
                    highlightedIndex={combobox.highlightedIndex}
                    filteredItems={combobox.filteredItems}
                  />
                )}
              </ComboboxCollection>
            </ComboboxList>
          </ListScrollArea>
        </ComboboxContent>
      )}
    </Combobox>
  );
}

const ListScrollArea = styled.div({
  maxHeight: 390, // ~10 items approx., adjust as needed
  overflowY: "auto",
});
